import React, {useMemo, useState} from 'react';
import {Link, Navigate} from 'react-router';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {useAuth} from '@common/auth/use-auth';
import {Trans} from '@ui/i18n/trans';
import {Product} from '@common/billing/product';
import {Price} from '@common/billing/price';
import {OnboardingPlanStep} from './steps/onboarding-plan-step';
import {OnboardingAccountStep} from './steps/onboarding-account-step';
import {OnboardingDoneStep} from './steps/onboarding-done-step';
import {OnboardingLayout} from './onboarding-layout';
import {getFromLocalStorage} from '@ui/utils/hooks/local-storage';

export type OnboardingStep = 'plan' | 'account' | 'payment' | 'done';

export interface SelectedPlan {
  product: Product;
  price: Price | null;
}

const FREE_STEPS: OnboardingStep[] = ['plan', 'account', 'done'];
const PAID_STEPS: OnboardingStep[] = ['plan', 'account', 'payment'];
const STEP_LABELS: Record<OnboardingStep, string> = {
  plan: 'Plan',
  account: 'Account',
  payment: 'Payment',
  done: 'Done',
};

export function OnboardingPage() {
  const navigate = useNavigate();
  const {isLoggedIn} = useAuth();

  const savedPlan = useMemo(
    () =>
      getFromLocalStorage<{productId: number; priceId: number | null}>(
        'be.onboarding.selected',
      ),
    [],
  );

  const [step, setStep] = useState<OnboardingStep>(
    savedPlan ? 'account' : 'plan',
  );
  const [selectedPlan, setSelectedPlan] = useState<SelectedPlan | null>(() => {
    if (!savedPlan) return null;
    return {
      product: {id: savedPlan.productId} as Product,
      price: savedPlan.priceId ? ({id: savedPlan.priceId} as Price) : null,
    };
  });

  // Only block already-logged-in users at the entry point (plan step)
  if (isLoggedIn && step === 'plan') {
    return <Navigate to="/" replace />;
  }

  function goTo(s: OnboardingStep) {
    setStep(s);
  }

  const visibleSteps = selectedPlan?.price ? PAID_STEPS : FREE_STEPS;

  return (
    <OnboardingLayout wide={step === 'plan'}>
      <StepIndicator currentStep={step} steps={visibleSteps} />

      {(step === 'plan' || step === 'account') && (
        <p className="mb-24 text-center text-sm text-muted">
          <Trans
            values={{
              a: parts => (
                <Link className="font-medium text-primary hover:underline" to="/login">
                  {parts}
                </Link>
              ),
            }}
            message="Already have an account? <a>Sign in.</a>"
          />
        </p>
      )}

      {step === 'plan' && (
        <OnboardingPlanStep
          onSelect={(product, price) => {
            setSelectedPlan({product, price});
            goTo('account');
          }}
        />
      )}

      {step === 'account' && (
        <OnboardingAccountStep
          selectedPlan={selectedPlan}
          onBack={() => goTo('plan')}
          onSuccess={() => {
            if (selectedPlan?.price) {
              navigate(`/checkout/${selectedPlan.product.id}/${selectedPlan.price.id}`);
            } else {
              goTo('done');
            }
          }}
        />
      )}

      {step === 'done' && (
        <OnboardingDoneStep />
      )}
    </OnboardingLayout>
  );
}

interface StepIndicatorProps {
  currentStep: OnboardingStep;
  steps: OnboardingStep[];
}
function StepIndicator({currentStep, steps}: StepIndicatorProps) {
  const currentIndex = steps.indexOf(currentStep);
  return (
    <div className="mb-32 flex items-center justify-center gap-0">
      {steps.map((s, i) => {
        const isCompleted = i < currentIndex;
        const isCurrent = i === currentIndex;
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center">
              <div
                className={`flex h-28 w-28 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                  isCompleted
                    ? 'bg-primary text-on-primary'
                    : isCurrent
                    ? 'border-2 border-primary bg text-primary'
                    : 'border-2 border-divider bg text-muted'
                }`}
              >
                {isCompleted ? '✓' : i + 1}
              </div>
              <span
                className={`mt-4 text-xs ${
                  isCurrent ? 'font-medium text-primary' : 'text-muted'
                }`}
              >
                {STEP_LABELS[s]}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`mb-20 h-1 w-40 flex-shrink-0 transition-colors ${
                  i < currentIndex ? 'bg-primary' : 'bg-divider'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
